import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", age_confirmed: false, mailing_opt_in: false });
  const [loading, setLoading] = useState(false);
  const { signupJwt } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();

  const pkg = params.get("pkg");
  const destination =
    loc.state?.from ||
    (pkg ? `/dashboard/book?pkg=${pkg}` : "/dashboard/intake");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.age_confirmed) return toast.error("You must confirm you are 21+.");
    setLoading(true);
    try {
      await signupJwt(form);
      toast.success("Account created. Welcome.");
      nav(destination, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-up failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="signup-page" className="font-body max-w-md mx-auto px-6 md:px-10 pt-32 pb-20 sm:pt-40">
      <span className="rc-eyebrow block text-center">Rebel List</span>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold text-center mt-4" style={{ letterSpacing: "-0.03em" }}>
        Join the Rebel List.
      </h1>
      <p className="mt-4 text-sm text-[color:var(--rc-text-muted)] text-center leading-relaxed">
        Free account for details, pricing and booking. Use any alias — I do not verify legal names.
      </p>

      <form onSubmit={submit} data-testid="signup-form" className="mt-10 space-y-4">
        <div>
          <label htmlFor="signup-name" className="block mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-[color:var(--rc-text-muted)]">Alias / first name</label>
          <input id="signup-name" data-testid="signup-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3" />
        </div>
        <div>
          <label htmlFor="signup-email" className="block mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-[color:var(--rc-text-muted)]">Email</label>
          <input id="signup-email" data-testid="signup-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3" />
        </div>
        <div>
          <label htmlFor="signup-password" className="block mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-[color:var(--rc-text-muted)]">Password</label>
          <input id="signup-password" data-testid="signup-password" type="password" required minLength={8} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-3" />
        </div>

        <label className="flex gap-3 items-start text-xs text-[color:var(--rc-text-muted)] leading-relaxed cursor-pointer select-none">
          <input data-testid="signup-age-confirm" type="checkbox" checked={form.age_confirmed} onChange={e => setForm({...form, age_confirmed: e.target.checked})} className="mt-1" />
          <span>I confirm I am 21 years of age or older and consent to adult content.</span>
        </label>

        <label className="flex gap-3 items-start text-xs text-[color:var(--rc-text-muted)] leading-relaxed cursor-pointer select-none">
          <input
            data-testid="signup-mailing-optin"
            type="checkbox"
            checked={form.mailing_opt_in}
            onChange={e => setForm({...form, mailing_opt_in: e.target.checked})}
            className="mt-1"
          />
          <span>Email me occasional updates about new cities and openings. Optional — leave unchecked for account only.</span>
        </label>

        <button data-testid="signup-submit" type="submit" disabled={loading} className="w-full rc-btn rc-btn-blue disabled:opacity-50">
          {loading ? "Creating…" : <>Create Account <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>

      <p className="mt-8 text-sm text-[color:var(--rc-text-muted)] text-center">
        Already a member? <Link to={pkg ? `/login?pkg=${pkg}` : "/login"} state={loc.state} data-testid="link-to-login" className="text-[#2d7cf6] underline-editorial font-medium">Sign in</Link>
      </p>
    </div>
  );
}
